import "./dashboard.css";
import openIcon from "../../assets/icons/ticket2.png";
import DateRangePicker from "../../components/DateRangePicker";
import API from "../../services/api";
import { getUser } from "../../services/authStore";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo } from "react";

export default function Dashboard() {
  const [statusCounts, setStatusCounts] = useState([]);
  const [typeCategoryCounts, setTypeCategoryCounts] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [hoverData, setHoverData] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const currentYear = new Date().getFullYear();
  const launchYear = 2026;
  const months = useMemo(
    () => [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    [],
  );

  // Generate year options
  const yearOptions = useMemo(() => {
    const options = [];
    for (let year = launchYear; year <= currentYear; year++) {
      options.push(year);
    }
    return options;
  }, [currentYear]);

  // Set default date range
  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setFromDate(firstDay.toISOString().split("T")[0]);
    setToDate(lastDay.toISOString().split("T")[0]);
  }, []);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async (from, to) => {
    if (!from || !to) return;

    setLoading(true);
    try {
      const user = getUser();
      const res = await API.get(
        `/dashboard/${user.company_id}/${user.user_id}/${user.role}?from_date=${from}&to_date=${to}`,
      );

      if (res.data) {
        setTotalCount(res.data.status_counts?.total_count || 0);
        setStatusCounts(
          Array.isArray(res.data.status_counts?.status_data)
            ? res.data.status_counts.status_data
            : [],
        );
        setTypeCategoryCounts(
          Array.isArray(res.data.type_category_counts)
            ? res.data.type_category_counts
            : [],
        );
        setMonthlyData(
          Array.isArray(res.data.yearly_counts?.data)
            ? res.data.yearly_counts.data
            : [],
        );
      }
    } catch (error) {
      console.error("Dashboard API error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch when dates change
  useEffect(() => {
    if (fromDate && toDate) {
      fetchDashboard(fromDate, toDate);
    }
  }, [fromDate, toDate, fetchDashboard]);

  // Optional: Set up polling for real-time updates (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      if (fromDate && toDate) {
        fetchDashboard(fromDate, toDate);
      }
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, [fromDate, toDate, fetchDashboard]);

  const handleDateChange = (start, end) => {
    setFromDate(start);
    setToDate(end);
  };

  // Group types into pairs for combined cards
  const typePairs = useMemo(() => {
    const pairs = [];
    for (let i = 0; i < typeCategoryCounts.length; i += 2) {
      pairs.push({
        id: `pair-${i}`,
        left: typeCategoryCounts[i],
        right: typeCategoryCounts[i + 1] || null,
      });
    }
    return pairs;
  }, [typeCategoryCounts]);

  const chartValues = useMemo(() => {
    return months.map((_, index) => {
      const found = monthlyData.find((m) => m.month === index + 1);
      return found ? found.count : 0;
    });
  }, [months, monthlyData]);

  const maxValue = useMemo(() => Math.max(...chartValues, 1), [chartValues]);

  // Determine layout type based on number of combined cards
  // const getLayoutType = useCallback((cardCount) => {
  //   return cardCount % 2 === 0 ? "bottom" : "split";
  // }, []);
  // const layoutType = getLayoutType(typePairs.length);

  return (
    <div className="dashboard-wrapper">
      <div className="header-stats"></div>
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div className="header-left">
            <button
              className="add-ticket-btn"
              onClick={() =>
                navigate("/tickets", {
                  state: { openCreateForm: true },
                })
              }
            >
              + Add Ticket
            </button>
          </div>
          <div className="datearea">
            <DateRangePicker
              onDateChange={handleDateChange}
              startDate={fromDate}
              endDate={toDate}
            />
          </div>
        </div>

        <div className="dashboard-body">
          {/* STATUS CARDS */}
          <div className="stats-row">
            {/* First card: Total Tickets */}
            <StatCard
              key="total-tickets"
              title="Total Tickets"
              number={totalCount}
              icon={openIcon}
              bg="#FFE0B2"
              onClick={() => navigate("/tickets")}
            />

            {/* Other status cards */}
            {statusCounts.map((item) => {
              const color = item.status_colour?.startsWith("#")
                ? item.status_colour
                : `#${item.status_colour}`;

              return (
                <StatCard
                  key={item.status_id}
                  title={item.status_name}
                  number={item.count}
                  icon={openIcon}
                  bg={color}
                  onClick={() =>
                    navigate("/tickets", {
                      state: {
                        statusId: item.status_id,
                        statusName: item.status_name,
                      },
                    })
                  }
                />
              );
            })}
          </div>

          {/* DYNAMIC LAYOUT BASED ON CARD COUNT */}
          {/* DYNAMIC LAYOUT BASED ON CARD COUNT */}
          {loading && typePairs.length === 0 ? (
            <div className="loading-container">Loading dashboard data...</div>
          ) : (
            <>
              {(() => {
                const total = typePairs.length;
                const isOdd = total % 2 !== 0;

                const mainCards = isOdd ? typePairs.slice(0, -1) : typePairs;
                const lastCard = isOdd ? typePairs[typePairs.length - 1] : null;

                return (
                  <div className="dynamic-layout">
                    {/* ✅ CASE 1: ONLY 1 CARD */}
                    {total === 1 && (
                      <div className="split-layout">
                        <CombinedTypeCard
                          leftType={typePairs[0].left}
                          rightType={typePairs[0].right}
                        />
                        <CompactBarChart
                          chartValues={chartValues}
                          maxValue={maxValue}
                          months={months}
                          hoverData={hoverData}
                          setHoverData={setHoverData}
                          tooltipPos={tooltipPos}
                          setTooltipPos={setTooltipPos}
                          selectedYear={selectedYear}
                          setSelectedYear={setSelectedYear}
                          yearOptions={yearOptions}
                        />
                      </div>
                    )}

                    {/* ✅ CASE 2: MULTIPLE CARDS */}
                    {total > 1 && (
                      <>
                        {/* 🔹 MAIN GRID */}
                        <div className="combined-cards-grid-full">
                          {mainCards.map((pair) => (
                            <CombinedTypeCard
                              key={pair.id}
                              leftType={pair.left}
                              rightType={pair.right}
                            />
                          ))}
                        </div>

                        {/* 🔹 ODD → LAST ROW CARD + GRAPH */}
                        {isOdd && lastCard && (
                          <div className="split-layout">
                            <CombinedTypeCard
                              leftType={lastCard.left}
                              rightType={lastCard.right}
                            />
                            <CompactBarChart
                              chartValues={chartValues}
                              maxValue={maxValue}
                              months={months}
                              hoverData={hoverData}
                              setHoverData={setHoverData}
                              tooltipPos={tooltipPos}
                              setTooltipPos={setTooltipPos}
                              selectedYear={selectedYear}
                              setSelectedYear={setSelectedYear}
                              yearOptions={yearOptions}
                            />
                          </div>
                        )}

                        {/* 🔹 EVEN → GRAPH FULL WIDTH */}
                        {!isOdd && (
                          <CompactBarChart
                            chartValues={chartValues}
                            maxValue={maxValue}
                            months={months}
                            hoverData={hoverData}
                            setHoverData={setHoverData}
                            tooltipPos={tooltipPos}
                            setTooltipPos={setTooltipPos}
                            selectedYear={selectedYear}
                            setSelectedYear={setSelectedYear}
                            yearOptions={yearOptions}
                            isFullWidth={true}
                          />
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* STATUS CARD */
function StatCard({ title, number, icon, bg, onClick }) {
  return (
    <div className="stat-card" onClick={onClick} style={{ cursor: "pointer" }}>
      <div>
        <div className="stat-title">{title}</div>
        <div className="stat-number">{number}</div>
      </div>
      <div className="icon" style={{ background: bg }}>
        {icon && <img src={icon} alt="" />}
      </div>
    </div>
  );
}

/* COMBINED TYPE CARD - Contains 2 types side by side */
function CombinedTypeCard({ leftType, rightType }) {
  return (
    <div className="combined-type-card">
      {/* Left Type Section */}
      <div className="type-section">
        <SingleTypeContent type={leftType} />
      </div>

      {/* Vertical Divider */}
      <div className="vertical-divider"></div>

      {/* Right Type Section */}
      <div className="type-section">
        {rightType ? (
          <SingleTypeContent type={rightType} />
        ) : (
          <div className="empty-type">
            <div className="empty-message">No Data Available</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* Single Type Content */
function SingleTypeContent({ type }) {
  const [hoverData, setHoverData] = useState(null);

  const total = type?.categories?.reduce((sum, c) => sum + c.count, 0) || 0;

  // Calculate donut chart data
  let start = 0;
  const parts =
    type?.categories?.map((cat) => {
      const percent = total ? (cat.count / total) * 100 : 0;
      const end = start + percent;
      const color = cat.category_colour?.startsWith("#")
        ? cat.category_colour
        : `#${cat.category_colour}`;
      const part = {
        id: cat.category_id,
        name: cat.category_name,
        count: cat.count,
        color,
        start,
        end,
      };
      start = end;
      return part;
    }) || [];

  const gradient = parts.length
    ? `conic-gradient(${parts
        .map((p) => `${p.color} ${p.start}% ${p.end}%`)
        .join(",")})`
    : "#e5e5e5";

  return (
    <div className="single-type-content">
      <div className="card-title">{type?.type_name || "No Data"}</div>

      <div className="type-details">
        {/* Left - Category List */}
        <div className="category-detail">
          {type?.categories?.map((cat) => {
            const color = cat.category_colour?.startsWith("#")
              ? cat.category_colour
              : `#${cat.category_colour}`;
            return (
              <div className="category-item" key={cat.category_id}>
                <div className="category-info">
                  <div className="category-dot" style={{ background: color }} />
                  <span className="category-name">{cat.category_name}</span>
                </div>
                <span className="category-count">{cat.count}</span>
              </div>
            );
          })}
          {(!type?.categories || type.categories.length === 0) && (
            <div className="no-categories">No categories available</div>
          )}
        </div>

        {/* Right - Donut Chart */}
        <div className="donut-wrapper">
          <div
            className="compact-donut"
            style={{ background: gradient }}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();

              const x = e.clientX - rect.left - rect.width / 2;
              const y = e.clientY - rect.top - rect.height / 2;

              let angle = Math.atan2(y, x) * (180 / Math.PI);

              // ✅ FIX: align with conic-gradient
              angle = angle + 90;

              if (angle < 0) angle += 360;
              if (angle >= 360) angle -= 360;

              const percent = (angle / 360) * 100;

              const found = parts.find(
                (p) => percent >= p.start && percent <= p.end,
              );

              setHoverData(found || null);
            }}
            onMouseLeave={() => setHoverData(null)}
          >
            {hoverData && (
              <div className="donut-tooltip">
                {hoverData.name}: {hoverData.count}
              </div>
            )}

            <div className="donut-center">
              <div className="donut-total">{total}</div>
              <div className="donut-label">Total</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* COMPACT BAR CHART CARD */
function CompactBarChart({
  chartValues,
  maxValue,
  months,
  hoverData,
  setHoverData,
  tooltipPos,
  setTooltipPos,
  selectedYear,
  setSelectedYear,
  yearOptions,
  isFullWidth = false,
}) {
  return (
    <div className={`compact-chart-card ${isFullWidth ? "full-width" : ""}`}>
      <div className="compact-chart-header">
        <div className="compact-chart-title">Month-wise Tickets</div>
      </div>

      <div className="compact-chart-container">
        {/* Y-Axis */}
        <div className="compact-y-axis">
          <span>100</span>
          <span>75</span>
          <span>50</span>
          <span>25</span>
          <span>10</span>
        </div>

        {/* Chart Area */}
        <div className="compact-chart-area">
          {/* Grid Lines */}
          {[25, 50, 75, 100].map((line, i) => (
            <div
              key={i}
              className="compact-grid-line"
              style={{ bottom: `${line}%` }}
            />
          ))}

          {/* Tooltip */}
          {hoverData && (
            <div
              className="compact-tooltip"
              style={{
                left: tooltipPos.x,
                top: tooltipPos.y - 35,
              }}
            >
              <div className="compact-tooltip-title">{hoverData.month}</div>
              <div className="compact-tooltip-value">
                {hoverData.value} tickets
              </div>
            </div>
          )}

          {/* Bars */}
          <div className="compact-bars">
            {chartValues.map((val, i) => (
              <div className="compact-bar-wrapper" key={i}>
                <div
                  className="compact-bar"
                  style={{
                    height: `${(val / 100) * 140}px`,
                    background: "#4c51d6",
                  }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget
                      .closest(".compact-chart-area")
                      .getBoundingClientRect();
                    setHoverData({ month: months[i], value: val });
                    setTooltipPos({
                      x: e.clientX - rect.left,
                      y: e.clientY - rect.top,
                    });
                  }}
                  onMouseLeave={() => setHoverData(null)}
                />
                <span className="compact-month">{months[i]}</span>
              </div>
            ))}
          </div>

          {/* X-Axis Line */}
          <div className="compact-x-line"></div>
        </div>
      </div>
    </div>
  );
}
